import { useState } from 'react'
import { PlayCircle } from 'lucide-react'
import { useGuidanceVideo } from '@/hooks/useGuidanceVideo'
import { GuidanceVideoModal } from './GuidanceVideoModal'
import type { GuidanceSurface } from '@/lib/guidanceSurfaces'
import { cn } from '@/lib/utils'

interface VideoHelpButtonProps {
  surface: GuidanceSurface
  className?: string
  /** Accessible label + tooltip. */
  label?: string
  /** Icon-only (no "Watch" text) — for tight headers. */
  iconOnly?: boolean
}

/**
 * Small "watch the walkthrough" affordance for a page/dialog. Renders nothing
 * unless a guidance video is published for the surface, so it's safe to drop
 * into every header — it appears only once an admin binds a video.
 */
export function VideoHelpButton({ surface, className, label = 'Watch a short walkthrough', iconOnly }: VideoHelpButtonProps) {
  const { data: material } = useGuidanceVideo(surface)
  const [open, setOpen] = useState(false)

  if (!material) return null

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
        title={label}
        aria-label={label}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-heritage-blue/30 bg-heritage-blue/5 font-medium text-heritage-blue transition-colors hover:bg-heritage-blue/10',
          iconOnly ? 'p-1' : 'px-2.5 py-1 text-[11px]',
          className,
        )}
      >
        <PlayCircle className="h-3.5 w-3.5" />
        {!iconOnly && <span>Watch</span>}
      </button>
      <GuidanceVideoModal material={material} open={open} onOpenChange={setOpen} />
    </>
  )
}
